/* eslint-disable max-lines */
import {
  WebSocketClientOptions,
  WsConnectionState,
  WsConnectionStatus,
} from '@hakimo-ui/hakimo/types';
import { proxy, Remote, wrap } from 'comlink';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { WebSocketClient } from './WebsocketClient.worker';

export function useWebsocketConnect(
  url: string,
  getAccessToken?: (ignoreCache?: boolean) => Promise<string>,
  websocketClientOptions?: WebSocketClientOptions
) {
  // Refs for the worker instance and the wrapped WebSocketClient
  const workerRef = useRef<Worker | null>(null);

  const clientRef = useRef<Remote<WebSocketClient> | null>(null);

  const [wsConnectionState, setWsConnectionState] = useState<WsConnectionState>(
    { status: WsConnectionStatus.PENDING, isDuplicateSession: false }
  );

  const subscribersRef = useRef<Record<string, Set<(data: any) => void>>>({});

  const connect = useCallback(
    async (client: Remote<WebSocketClient>) => {
      try {
        let accessToken: string | undefined;
        if (getAccessToken) {
          accessToken = await getAccessToken();
        }
        await client.connect(url, accessToken);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('In hook Error connecting to WebSocket', error);
      }
    },
    [getAccessToken, url]
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    async function initializeWebSocketClient() {
      // Use webpack 5's native Web Worker support
      const workerInstance = new Worker(
        new URL('./WebsocketClient.worker.ts', import.meta.url)
      );
      const client = wrap<WebSocketClient>(workerInstance);

      workerRef.current = workerInstance;
      clientRef.current = client;

      client.setInitOptions(websocketClientOptions);

      client.setConnectionStateHandler(
        proxy((data) => {
          setWsConnectionState(data);
        })
      );
      client.setMessageHandler(
        // Use Comlink.proxy to wrap the callback function
        proxy((message: any) => {
          try {
            const { type, data } = message;
            const subscribers = subscribersRef.current[type];
            if (subscribers) {
              subscribers.forEach((callback) => callback(data));
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error handling message:', { message, error });
          }
        })
      );

      if (getAccessToken) {
        client.setTokenHandler(
          proxy(async () => {
            return await getAccessToken(true);
          })
        );
      }

      connect(client);
    }

    initializeWebSocketClient();

    return () => {
      // Cleanup: disconnect the client, and terminate the worker.
      if (clientRef.current) {
        clientRef.current.disconnect();
      }
      workerRef.current?.terminate();
    };
  }, [connect, websocketClientOptions]);

  const disconnect = () => {
    // eslint-disable-next-line no-console
    console.log('Disconnecting WebSocket client by user');
    clientRef.current?.disconnect();
    workerRef.current?.terminate();
  };

  // Function to subscribe to messages for a given topic (i.e. message.type)
  // Returns an unsubscribe function.
  const subscribe = useCallback(
    (topic: string, callback: (data: any) => void): (() => void) => {
      if (!subscribersRef.current[topic]) {
        subscribersRef.current[topic] = new Set();
      }
      subscribersRef.current[topic].add(callback);

      return () => {
        subscribersRef.current[topic]?.delete(callback);
      };
    },
    []
  );

  const send = (message: any) => {
    if (clientRef.current) {
      const msgStr =
        typeof message === 'string' ? message : JSON.stringify(message);
      clientRef.current.send(msgStr);
    } else {
      // eslint-disable-next-line no-console
      console.error('WebSocket client not initialized');
    }
  };

  const reconnect = () => {
    const client = clientRef.current;
    if (client) {
      connect(client);
    }
  };

  useEffect(() => {
    if (!getAccessToken) {
      return;
    }

    const handleTokenRefreshRequest = async () => {
      try {
        const newToken = await getAccessToken(true);
        if (clientRef.current) {
          const refreshResponse = {
            event_type: 'token_refresh_response',
            token: newToken,
          };
          clientRef.current.send(JSON.stringify(refreshResponse));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error refreshing token:', error);
      }
    };
    const unsubscribe = subscribe(
      'token_refresh_request',
      handleTokenRefreshRequest
    );
    return () => {
      unsubscribe();
    };
  }, [getAccessToken, subscribe]);

  return { wsConnectionState, subscribe, send, disconnect, reconnect };
}
